import {
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import * as Yup from "yup";
import { useSnack } from "../../../services/SnakbarProvider";
import { handleError } from "../../../services/errorHandler";
import { forgotPassword } from "../../../api/auth";

function ForgotPasswordForm() {
  const { openSnack } = useSnack();
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    email: Yup.string("Ingresa un email válido")
      .email("Ingresa un email válido")
      .required("El email es requerido"),
  });

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await forgotPassword(values.email);
        openSnack(
          "Se ha enviado un correo con las instrucciones para restablecer tu contraseña",
          "success"
        );
        navigate("/login");
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        bgcolor: "#fff",
        border: "1px solid rgba(2, 83, 115, 0.08)",
        boxShadow: "0 18px 48px rgba(2, 40, 64, 0.08)",
        fontFamily: '"Manrope", sans-serif',
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: '"Sora", sans-serif',
          fontWeight: 700,
          fontSize: { xs: "1.45rem", sm: "1.65rem" },
          letterSpacing: "-0.02em",
          color: "#022840",
          lineHeight: 1.25,
        }}
      >
        Recuperar contraseña
      </Typography>
      <Typography
        sx={{
          mt: 1,
          mb: 3,
          color: "rgba(2, 40, 64, 0.65)",
          fontSize: "0.95rem",
          lineHeight: 1.5,
        }}
      >
        Ingresa el correo asociado a tu cuenta y te enviaremos las
        instrucciones para restablecerla.
      </Typography>

      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        <TextField
          required
          fullWidth
          id="email"
          label="Correo electrónico"
          name="email"
          autoComplete="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineRoundedIcon
                  sx={{ color: "rgba(2, 83, 115, 0.55)", fontSize: 22 }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "#F8FBFC",
              transition: "background-color 0.2s ease, box-shadow 0.2s ease",
              "&:hover": { bgcolor: "#fff" },
              "&.Mui-focused": {
                bgcolor: "#fff",
                boxShadow: "0 0 0 3px rgba(3, 127, 140, 0.18)",
              },
              "& fieldset": {
                borderColor: "rgba(2, 83, 115, 0.18)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(2, 83, 115, 0.35)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#037F8C",
                borderWidth: 1.5,
              },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#025373",
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={formik.isSubmitting}
          sx={{
            mt: 3,
            mb: 1.5,
            py: 1.35,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.98rem",
            fontFamily: '"Manrope", sans-serif',
            background: "linear-gradient(135deg, #025373 0%, #037F8C 100%)",
            boxShadow: "0 10px 24px rgba(2, 83, 115, 0.28)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              background: "linear-gradient(135deg, #022840 0%, #025373 100%)",
              boxShadow: "0 12px 28px rgba(2, 40, 64, 0.32)",
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": {
              background: "rgba(2, 83, 115, 0.35)",
              color: "rgba(255,255,255,0.85)",
            },
          }}
        >
          {formik.isSubmitting ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            "Enviar instrucciones"
          )}
        </Button>

        <Button
          fullWidth
          variant="text"
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate("/login")}
          sx={{
            mt: 0.5,
            py: 1,
            textTransform: "none",
            fontWeight: 500,
            fontFamily: '"Manrope", sans-serif',
            color: "rgba(2, 40, 64, 0.65)",
            borderRadius: 2,
            "&:hover": {
              bgcolor: "rgba(2, 83, 115, 0.06)",
              color: "#025373",
            },
          }}
        >
          Volver al inicio de sesión
        </Button>
      </Box>
    </Box>
  );
}

export default ForgotPasswordForm;
